import { useQuery } from '@tanstack/react-query';
import { catalogueKeys, fetchProducts } from '@/api/catalogue';
import { ProductCard } from '@/components/product/ProductCard';
import { SectionHeading } from '@/components/home/SectionHeading';
import { PRODUCT_GRID_CLASS } from '@/components/product/grid';
import { ProductCardSkeleton, SkeletonRegion } from '@/components/ui/Skeleton';
import type { ProductFilters } from '@/types/catalogue';

/**
 * A homepage row of products drawn from the live catalogue —
 * BUILD-PLAN §11, §41, §86.
 *
 * Takes the same filters the shop uses, so "Best Sellers" and "Featured" are
 * genuinely the products marked as such in admin — not a curated hard-coded
 * list that silently goes stale.
 *
 * If the query fails or returns nothing, the whole section is omitted. §86 is
 * explicit that a section must not pretend to have content, and an absent row
 * is a better homepage than an error card or an empty shelf.
 */

interface ProductShowcaseProps {
  /** Editorial index, forwarded to SectionHeading. */
  index?: number;
  eyebrow?: string;
  title: string;
  description?: string;
  filters: ProductFilters;
  link?: { to: string; label: string };
  limit?: number;
}

export function ProductShowcase({
  index,
  eyebrow,
  title,
  description,
  filters,
  link,
  limit = 4,
}: ProductShowcaseProps) {
  const query = { ...filters, per_page: limit };

  const { data, isPending, isError } = useQuery({
    queryKey: catalogueKeys.products(query),
    queryFn: () => fetchProducts(query),
    staleTime: 5 * 60 * 1000,
  });

  if (isError) {
    return null;
  }

  if (isPending) {
    return (
      <section>
        <SectionHeading
          {...(index !== undefined ? { index } : {})}
          {...(eyebrow ? { eyebrow } : {})}
          title={title}
        />

        {/*
          The SHARED skeleton, not a local copy.
          ------------------------------------------------------------------
          What stood here was hand-rolled and painted `bg-surface-muted`,
          which the theme defines as #f9f0e7 — against a #f8efe6 page
          background. One value per channel. The skeletons rendered, animated
          and were invisible, so the homepage appeared to have no loading
          state at all while in fact having a complete one.

          `components/ui/Skeleton.tsx` already solved this (it paints
          `bg-ink/[0.09]`, a tint of the text colour, which cannot collide
          with the background). Sharing it also guarantees the placeholder
          uses the same column ramp as the real grid — a skeleton that
          disagrees with its grid makes the page jump at the moment content
          arrives, which is worse than showing nothing.
        */}
        <SkeletonRegion label={`Loading ${title}`}>
          <div className={`mt-10 ${PRODUCT_GRID_CLASS}`}>
            {Array.from({ length: limit }, (_, position) => (
              <ProductCardSkeleton key={position} />
            ))}
          </div>
        </SkeletonRegion>
      </section>
    );
  }

  const products = data?.items ?? [];

  if (products.length === 0) {
    return null;
  }

  return (
    <section>
      <SectionHeading
        {...(index !== undefined ? { index } : {})}
        {...(eyebrow ? { eyebrow } : {})}
        title={title}
        {...(description ? { description } : {})}
        {...(link ? { link } : {})}
      />

      <div className={`mt-10 ${PRODUCT_GRID_CLASS}`}>
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
}
